// Account + history panel (plan U7–U10 UI; sign-in-menu design 2026-06-04).
// Renders on the analytics-free home (R22). The top menu owns the logged-out CTA
// and sign-out; this panel only renders when signed in and drives passphrase
// setup/unlock, recovery, and My History, lifting the unlocked account up so
// logged-in shares can be saved to history.

import { useEffect, useState } from "react";
import {
  setupAccountFlow,
  unlockAccountFlow,
  verifyRecoveryCode,
  rotateAfterRecovery,
  fetchHistoryDecrypted,
  deleteHistoryEntry,
  saveToHistory,
  setHistoryShareState,
  renameHistoryEntry,
  type UnlockedAccount,
  type DecryptedHistoryItem,
} from "./account-client";
import {
  CopyableSecret,
  RegenerateButton,
  HistoryRow,
  CliTokensTab,
} from "./components";
import { generatePassphrase } from "./passphrase-gen";
import { rememberAccount, recallAccount } from "./account-remember";
import { fetchWithSession } from "./session-id";

type Phase = "setup" | "unlock" | "recover" | "ready";

export function AccountPanel({
  signedIn,
  login,
  onAccount,
  currentSession = "",
}: {
  signedIn: boolean;
  login: string | null;
  onAccount?: (account: UnlockedAccount | null) => void;
  /** Current textarea content (owned by App) — what "Save current session" stores. */
  currentSession?: string;
}) {
  // `null` while the /api/account probe is in flight (nothing is rendered yet).
  const [phase, setPhase] = useState<Phase | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [shownRecovery, setShownRecovery] = useState<string | null>(null);
  const [history, setHistory] = useState<DecryptedHistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Setup- and recovery-phase passphrase generation (shared `generated` state;
  // the two phases are mutually exclusive).
  const [generated, setGenerated] = useState("");
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  // Gates the recovery-code "Confirm" button (parallel to savedConfirmed for setup).
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);

  // Recover phase: the recovery code is verified against the account (read-only,
  // no server write) before the new-passphrase generator unlocks. `verifiedKey`
  // holds the unlocked account key so the rotation reuses it without re-fetching.
  const [verifiedKey, setVerifiedKey] = useState<CryptoKey | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Ready-phase tab: see shared pastes (history) or manage CLI tokens.
  // The unlocked account key is held to mint tokens.
  const [tab, setTab] = useState<"history" | "cli">("history");
  const [accountKey, setAccountKey] = useState<CryptoKey | null>(null);
  // Collapse the ready-phase panel body (tab content) while keeping the header
  // — with its tabs, the Save action, and the chevron — in view.
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    // Signed out (or signing out): reset everything so a later sign-in is clean.
    // App owns the account state and already cleared it via onAccount(null).
    if (!signedIn) {
      setPhase(null);
      setPassphrase("");
      setRecoveryCode("");
      setShownRecovery(null);
      setHistory([]);
      setError(null);
      setGenerated("");
      setSavedConfirmed(false);
      setAccountKey(null);
      setVerifiedKey(null);
      setVerifying(false);
      setVerifyError(null);
      return;
    }

    // Identity is known. If this device remembers the key for the signed-in user,
    // auto-unlock and skip the passphrase. Otherwise probe key-custody: 404 (no
    // account → setup) or 200 (account exists → unlock).
    let cancelled = false;
    (async () => {
      if (login) {
        const recalled = await recallAccount(login);
        if (recalled && !cancelled) {
          await afterUnlock({ accountKey: recalled, authTag: "" }, false);
          return;
        }
      }
      try {
        const r = await fetchWithSession("/api/account", undefined, "home");
        if (cancelled) return;
        if (r.status === 404) {
          setSavedConfirmed(false);
          regenerate();
          setPhase("setup");
        } else if (r.ok) {
          setPhase("unlock");
        } else {
          setError("Could not load your account — please sign in again.");
        }
      } catch {
        if (!cancelled) setError("Could not reach the server — check your connection.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, login]);

  // `store` is false only on the auto-unlock (recall) path, which already came
  // from storage. doSetup/doUnlock/doRecover use the default (store = true).
  async function afterUnlock(account: UnlockedAccount, store = true) {
    if (store && login) void rememberAccount(login, account.accountKey);
    setAccountKey(account.accountKey);
    onAccount?.(account);
    try {
      setHistory(await fetchHistoryDecrypted(account.accountKey));
    } catch {
      /* history is best-effort */
    }
    setPhase("ready");
  }

  async function run(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function reloadHistory() {
    if (!accountKey) return;
    try {
      setHistory(await fetchHistoryDecrypted(accountKey));
    } catch {
      /* best-effort */
    }
  }

  const saveCurrent = () =>
    run(async () => {
      if (!accountKey || !currentSession.trim()) return;
      await saveToHistory(currentSession, accountKey, {});
      await reloadHistory();
    });

  const setRowState = (h: DecryptedHistoryItem, state: "active" | "private") =>
    run(async () => {
      await setHistoryShareState(h.shareId, state);
      setHistory((prev) => prev.map((x) => (x.shareId === h.shareId ? { ...x, state } : x)));
    });

  const renameRow = (h: DecryptedHistoryItem, title: string) =>
    run(async () => {
      if (!h.keyFragment) return;
      // Empty clears the title: the row falls back to the id-as-title treatment
      // (a null title) rather than a literal "(untitled)" string.
      const next = title.trim();
      await renameHistoryEntry(h.shareId, h.keyFragment, next);
      setHistory((prev) => prev.map((x) => (x.shareId === h.shareId ? { ...x, title: next || null } : x)));
    });

  const deleteRow = (h: DecryptedHistoryItem) =>
    run(async () => {
      await deleteHistoryEntry(h.shareId);
      setHistory((prev) => prev.filter((x) => x.shareId !== h.shareId));
    });

  // Never weaken entropy silently: if crypto is unavailable, surface the error
  // rather than falling back to a weak source.
  function regenerate() {
    try {
      setGenerated(generatePassphrase());
      setSavedConfirmed(false);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Editing the recovery code invalidates any prior verification: the unlocked
  // key (and the generated passphrase it gated) belonged to the old code.
  function onRecoveryCodeChange(value: string) {
    setRecoveryCode(value);
    if (verifiedKey) setVerifiedKey(null);
    if (verifyError) setVerifyError(null);
  }

  // Read-only check of the recovery code (no server write); on success it unlocks
  // the new-passphrase generator. Runs on blur / Enter, skipping empty input and
  // codes already verified.
  async function verifyRecovery() {
    const code = recoveryCode.trim();
    if (!code || verifying || verifiedKey) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const key = await verifyRecoveryCode(code);
      setVerifiedKey(key);
      regenerate(); // populate the first new passphrase now that the code is accepted
    } catch {
      setVerifiedKey(null);
      setVerifyError("That recovery code isn't valid. Check it and try again.");
    } finally {
      setVerifying(false);
    }
  }

  const doSetup = () =>
    run(async () => {
      const { recoveryCode: code, account } = await setupAccountFlow(generated);
      setRecoveryConfirmed(false);
      setShownRecovery(code);
      await afterUnlock(account);
    });
  const doUnlock = () =>
    run(async () => afterUnlock(await unlockAccountFlow(passphrase)));
  const doRecover = () =>
    run(async () => {
      if (!verifiedKey) throw new Error("Verify your recovery code first.");
      const { recoveryCode: code, account } = await rotateAfterRecovery(
        recoveryCode.trim(),
        verifiedKey,
        generated,
      );
      setRecoveryConfirmed(false);
      setShownRecovery(code);
      await afterUnlock(account);
    });

  if (!signedIn) return null;

  return (
    <div className="account-panel">

      {phase === "setup" && (
        <div>
          <p>
            We generated a strong passphrase for your encrypted history. Save it
            in your password manager — we cannot reset it.
          </p>
          <CopyableSecret value={generated} actions={<RegenerateButton onClick={regenerate} />} />
          <label className="saved-confirm">
            <input
              type="checkbox"
              checked={savedConfirmed}
              onChange={(e) => setSavedConfirmed(e.target.checked)}
            />
            I&apos;ve saved this to my password manager
          </label>
          <button onClick={doSetup} disabled={busy || !savedConfirmed}>
            {busy ? "Securing…" : "Secure my data"}
          </button>
        </div>
      )}

      {phase === "unlock" && (
        <div className="input-row">
          <input
            type="text"
            placeholder="Paste your passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
          />
          <button onClick={doUnlock} disabled={busy}>
            {busy ? "Unlocking…" : "Unlock history"}
          </button>
          <button className="button-ghost" onClick={() => setPhase("recover")}>
            Lost passphrase?
          </button>
        </div>
      )}

      {phase === "recover" && (
        <div className="recover-flow">
          <p>Enter your recovery code, then generate a new passphrase.</p>

          {/* 1. Recovery code — verified against your account before the rest unlocks. */}
          <div className="recover-step">
            <input
              className="recovery-code-input"
              placeholder="Recovery code"
              value={recoveryCode}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-invalid={verifyError ? true : undefined}
              onChange={(e) => onRecoveryCodeChange(e.target.value)}
              onBlur={verifyRecovery}
              onKeyDown={(e) => {
                if (e.key === "Enter") verifyRecovery();
              }}
            />
            {verifying ? (
              <span className="recover-status">Checking…</span>
            ) : verifiedKey ? (
              <span className="recover-status ok">Recovery code accepted</span>
            ) : verifyError ? (
              <span className="recover-status error">{verifyError}</span>
            ) : null}
          </div>

          {/* 2. New passphrase — same generator as setup, active once the code is accepted. */}
          {verifiedKey ? (
            <CopyableSecret
              value={generated}
              actions={<RegenerateButton onClick={regenerate} />}
            />
          ) : (
            <div className="secret-row is-disabled" aria-disabled="true">
              <div className="secret-box placeholder">
                New passphrase — verify your recovery code first
              </div>
              <button type="button" className="icon-button" disabled aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M21 3v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}

          {/* 3. Recover. */}
          <button onClick={doRecover} disabled={busy || !verifiedKey || !generated}>
            {busy ? "Recovering…" : "Recover"}
          </button>
        </div>
      )}

      {phase === "ready" && (
        <div>
          {shownRecovery ? (
            <div className="recovery-code">
              <p>
                <strong>Save your recovery code</strong> — it is shown once and is
                the only way back if you forget your passphrase:
              </p>
              <CopyableSecret value={shownRecovery} />
              <label className="saved-confirm">
                <input
                  type="checkbox"
                  checked={recoveryConfirmed}
                  onChange={(e) => setRecoveryConfirmed(e.target.checked)}
                />
                I&apos;ve saved my recovery code
              </label>
              <button
                onClick={() => setShownRecovery(null)}
                disabled={!recoveryConfirmed}
              >
                Confirm
              </button>
            </div>
          ) : (
          <>
          <div className="panel-header">
            <div className="panel-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === "history"}
                className={tab === "history" ? "panel-tab active" : "panel-tab"}
                onClick={() => { setTab("history"); setCollapsed(false); }}>History</button>
              <button type="button" role="tab" aria-selected={tab === "cli"}
                className={tab === "cli" ? "panel-tab active" : "panel-tab"}
                onClick={() => { setTab("cli"); setCollapsed(false); }}>CLI</button>
            </div>
            <div className="panel-header-actions">
              {tab === "history" && (
                <button
                  type="button"
                  className="cli-add-button"
                  disabled={busy || !currentSession.trim()}
                  title={currentSession.trim() ? "Save the current session to your history" : "Paste or upload a session below first"}
                  onClick={() => { setCollapsed(false); saveCurrent(); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Save current session
                </button>
              )}
              <button
                type="button"
                className="panel-collapse-toggle"
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Expand panel" : "Collapse panel"}
                title={collapsed ? "Expand" : "Collapse"}
                onClick={() => setCollapsed((c) => !c)}
              >
                <svg
                  className={collapsed ? "chevron is-collapsed" : "chevron"}
                  width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
          {collapsed ? null : tab === "cli" ? (
            accountKey && <CliTokensTab accountKey={accountKey} />
          ) : (
            <div className="history-pane">
              {history.length === 0 ? (
                <p>No saved sessions yet. Save one above, then toggle Share to get a link.</p>
              ) : (
                <ul className="history-list">
                  {history.map((h) => (
                    <li key={h.shareId}>
                      <HistoryRow
                        item={h}
                        onSetState={(state) => setRowState(h, state)}
                        onRename={(title) => renameRow(h, title)}
                        onDelete={() => deleteRow(h)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          </>
          )}
        </div>
      )}
      {error && <div className="validation-error">{error}</div>}
    </div>
  );
}
